import type { Amount } from '@shared/core-types';

import type {
    TransactionAccountInfo,
    TransactionAdditionalData,
    TransactionAmountAccounting,
    TransactionBalance,
    TransactionBookingInfo,
    TransactionBulkInfo,
    TransactionCategorization,
    TransactionCreditorDebtorInfo,
    TransactionDateInfo,
    TransactionExchangeInfo,
    TransactionFee,
    TransactionForInvestment,
    TransactionFromCard,
    TransactionGeneratedText,
    TransactionLocationInfo,
    TransactionMerchantInfo,
    TransactionOptionalText,
    TransactionOriginatorInfo,
    TransactionPartnerInfo,
    TransactionPaymentInfo,
    TransactionReceiverInfo,
    TransactionRounding,
    TransactionSavings,
    TransactionSenderInfo,
    TransactionSepaInfo,
    TransactionTaxPayments,
    TransactionUserActions,
    TransactionVerificationPayee,
    TransactionWithAttachment,
} from './TransactionMembers';
import type { TransactionsContext } from './TransactionsContext';

export interface Transaction
    extends TransactionWithAttachment,
        TransactionAccountInfo,
        TransactionFromCard,
        TransactionPaymentInfo,
        TransactionReceiverInfo,
        TransactionPartnerInfo,
        TransactionSenderInfo,
        TransactionGeneratedText,
        TransactionOptionalText,
        TransactionSepaInfo,
        TransactionForInvestment,
        TransactionBookingInfo,
        TransactionAmountAccounting,
        TransactionSavings,
        TransactionLocationInfo,
        TransactionCategorization,
        TransactionBalance,
        TransactionOriginatorInfo,
        TransactionDateInfo,
        TransactionExchangeInfo,
        TransactionFee,
        TransactionBulkInfo,
        TransactionCreditorDebtorInfo,
        TransactionRounding,
        TransactionTaxPayments,
        TransactionMerchantInfo,
        TransactionVerificationPayee,
        TransactionUserActions {
    id: string;
    logo: Logo;
    flags: TransactionFlag[];
    txType: TransactionType;
    txDirection: TransactionDirection;
    similarityKey: string;
    instantPayment?: boolean;
    additionalData?: TransactionAdditionalData;
    billId?: number | null;
    sequenceNumber?: string | null;
}

export interface Logo {
    /** @deprecated https://gapi-spec.g-labs.io/#/transactions/TransactionResource_search_get */
    id?: number;
    uri: string;
    requiresAuthentication: boolean;
}

export interface AdditionalTexts {
    text1?: string | null;
    text2?: string | null;
    text3?: string | null;
    variableSymbol?: string | null;
    constantSymbol?: string | null;
    specificSymbol?: string | null;
    lineItems?: string[] | null;
}

export interface Account {
    id: string;
    iban?: string | null;
    alias?: string | null;
    productType?: TransactionsContext;
    routingProductId?: string | null;
}

export interface Partner {
    iban: string;
    bic: string;
    bankCode: string;
    number: string;
    displayAccountNumber?: string;
    prefix: string;
    secondaryId?: {
        type: 'EMAIL' | 'PHONE' | 'TAX_ID' | 'TAX_NO';
        id: string;
    };
}

export interface VirtualCard {
    virtualCardNumber: string;
    deviceName: string;
    mobilePaymentApplicationName: string;
}

export interface Fee {
    amount?: Amount | null;
    type?: string | null;
}

export interface StructuredAddress {
    department?: string | null;
    subDepartment?: string | null;
    streetName?: string | null;
    buildingNumber?: string | null;
    buildingName?: string | null;
    floor?: string | null;
    postBox?: string | null;
    room?: string | null;
    postCode?: string | null;
    townName?: string | null;
    townLocationName?: string | null;
    districtName?: string | null;
    countrySubDivision?: string | null;
    countryNation?: string | null;
}

export enum RoundingType {
    SAVING = 'SAVING',
    DONATION = 'DONATION',
}

export enum TransactionDirection {
    ALL = '',
    INCOMING = 'INCOMING',
    OUTGOING = 'OUTGOING',
}

export enum TransactionOriginatorSystem {
    MULTI_BANKING = 'MBS',
    EBICS = 'EBX',
    THIRD_PARTY_APPLICATION = 'THIRD_PARTY_APPLICATION',
    THIRD_PARTY_APPLICAT = 'THIRD_PARTY_APPLICAT',
    SAIFU = 'SAIFU',
    POKETKA = 'POKETKA',
    POKETKA_PROD = 'POKETKA_PROD',
    POKETKA_BETA = 'POKETKA_BETA',
    FRIENDS24 = 'FRIENDS24',
    PLATBA24 = 'PLATBA24',
    GEORGEGO = 'GEORGEGO',
    GEORGE = 'GEORGE',
    GEORGE_GO = 'GEORGE_GO',
    ATM_OTHER = 'ATM_OTHER',
    GSM = 'GSM',
    MELINDA = 'MELINDA',
    UNKNOWN = 'UNKNOWN',
    ATM_PAYMENT = 'ATM_PAYMENT',
    BRANCH_FE = 'BRANCH_FE',
    POST = 'POST',
    INTERNET_BANKING = 'INTERNET_BANKING',
    TELEPHONE_BANKER = 'TELEPHONE_BANKER',
    BUSINESS24 = 'BUSINESS24',
    COLLECTION_BOX = 'COLLECTION_BOX',
    VIDEO_BANKER = 'VIDEO_BANKER',
    NET_BANKING = 'NET_BANKING',
}

export enum TransactionType {
    // A transaction is direct debit (CASHCOLLECTION) when it is withdrawing
    // money from another account instead of sending
    CASH_COLLECTION = 'CASHCOLLECTION',
    BANK_INFO = 'BANKINFO',
    ATM = 'ATM',
    ROUNDINGUP_SAVING = 'ROUNDINGUPSAVING',
    POS_PAYMENT = 'POSPAYMENT',
    BANK_FEE = 'BANKFEE',
    MOBILE_PAYMENT = 'MOBILEPAYMENT',
    OTHER = 'OTHER',
    BULK = 'BULK',
    PREPAID_CARD = 'PREPAIDCARD',
    CREDIT_CARD = 'CREDITCARD',
    STANDINGORDER = 'STANDINGORDER',
    INSTANT_PAYMENT = 'INSTANTPAYMENT',
    FOREIGN = 'FOREIGN',
    MANUAL = 'MANUAL',
    CREDIT_CARD_COLLECTION = 'CREDITCARDCOLLECTION',
    SIPO = 'SIPO',
    SECURITIES = 'SECURITIES',
}

export enum SepaScheme {
    INSTANT_PAYMENT = 'INST',
    B2B = 'B2B',
    CORE = 'CORE',
}

export enum TransactionFlag {
    HAS_VOUCHER = 'HAS_VOUCHER',
    HAS_IMAGE = 'HAS_IMAGE',
    HAS_INSTANT_PAYMENT_LOGO = 'HAS_INSTANT_PAYMENT_LOGO',

    IS_CLAIMABLE_FEE = 'IS_CLAIMABLE_FEE',
    IS_CLAIMABLE = 'IS_CLAIMABLE',
    IS_RETRIEVABLE = 'IS_RETRIEVABLE',
    IS_ARCHIVED = 'IS_ARCHIVED',
    IS_FAILED = 'IS_FAILED',
    IS_BAL_RELEVANT = 'IS_BAL_RELEVANT',
    IS_VISIBLE = 'IS_VISIBLE',
    IS_POSTAL_QR = 'IS_POSTAL_QR',
    IS_DEEPLINK_PAYMENT = 'IS_DEEPLINK_PAYMENT',

    ALLOWS_DIRECT_DEBIT_REDO = 'ALLOWS_DIRECT_DEBIT_REDO',
    ALLOWS_TRANSFER_REDO = 'ALLOWS_TRANSFER_REDO',
    ALLOWS_REPLY = 'ALLOWS_REPLY',
}

export enum BookingType {
    VEROPAY = 'VEROPAY',
    HCT_CASH_IN_NOA = 'HCT_CASH_IN_NOA',
    HCT_CASH_OUT_NOA = 'HCT_CASH_OUT_NOA',
    HCT_POS_OUT_NOA = 'HCT_POS_OUT_NOA',
    HSD_SEPA_OUT_NOA = 'HSD_SEPA_OUT_NOA',
    EL_SPENDE_EPA = 'EL-SPENDE-EPA',
    EL_SPENDE_SB = 'EL-SPENDE-SB',
    SBEIGEN = 'SBEIGEN',
    SBEIGENS = 'SBEIGENS',
    SBEIGENSUM = 'SBEIGENSUM',
    NBFAMT = 'NBFAMT',
    MBFAMT = 'MBFAMT',
    GEFAMT = 'GE-FAMT',
    TAFAMT = 'TA-FAMT',
    TPPFAMT = 'TPP-FAMT',
}

export enum VerificationPayeeResult {
    VERIFIED_NAME = 'VERIFIED_NAME',
    SIMILAR_NAME = 'SIMILAR_NAME',
    CHECK_NAME = 'CHECK_NAME',
    NOT_AVAILABLE = 'NOT_AVAILABLE',
    NOT_SUPPORTED = 'NOT_SUPPORTED',
}

export interface TransactionAttachment {
    name: string;
    size: number;
    hasThumbnail: boolean;
    type: string;
}

export enum TransactionOrderCategory {
    DOMESTIC = 'DOMESTIC',
    SEPA = 'SEPA',
    INTERNATIONAL = 'INTERNATIONAL',
}

export enum TransactionOrderRole {
    RECEIVER = 'RECEIVER',
    SENDER = 'SENDER',
}

export interface TaxPayment {
    year: number;
    startMonth: number | null;
    endMonth: number | null;
    credits: TaxAmount[];
    charges: TaxAmount[];
}

export interface TaxAmount {
    type: string;
    amount: Amount;
}

export interface SavingsOriginalTransaction {
    id: Transaction['id'];
    amount: Amount | null;
    transactionDate: number | string | null;
    partnerName: string | null;
    merchantTown: string | null;
    merchantCountry: string | null;
}

export interface UserAction {
    userFullName: string;
    timestamp: number;
}
